/**
 * IKEntryHistoryModal
 * ====================
 * Version history modal for Internal Knowledge entries.
 * Left panel: version list, right panel: version details.
 * Same pattern as KBEntryHistoryModal.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../hooks';
import { getIKEntryHistory, revertIKEntry } from '../../api/internalKnowledge';

const CONFIDENCE_COLORS = {
  high: 'bg-green-100 text-green-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-red-100 text-red-700',
};

const IKEntryHistoryModal = ({ entryId, entryName, onClose, onReverted }) => {
  const { showToast } = useToast();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [revertTarget, setRevertTarget] = useState(null);
  const [reverting, setReverting] = useState(false);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getIKEntryHistory(entryId);
      const v = res.data?.versions || [];
      setVersions(v);
      if (v.length > 0) setSelectedVersion(v[0]);
    } catch {
      showToast('Failed to load version history', 'error');
    } finally {
      setLoading(false);
    }
  }, [entryId, showToast]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleRevert = async () => {
    if (!revertTarget) return;
    setReverting(true);
    try {
      await revertIKEntry(entryId, revertTarget);
      showToast(`Reverted to version ${revertTarget}`, 'success');
      setRevertTarget(null);
      onReverted?.();
      onClose();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to revert', 'error');
    } finally {
      setReverting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-strong max-w-5xl w-full max-h-[85vh] flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
            <div>
              <h2 className="text-xl font-almarai font-normal text-primary">Version History</h2>
              <p className="text-sm text-gray-500 mt-0.5">{entryName}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
            </div>
          ) : versions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-gray-500 text-sm">No version history yet.</p>
            </div>
          ) : (
            <div className="flex flex-1 min-h-0">
              {/* Left: version list */}
              <div className="w-72 border-r border-gray-200 overflow-y-auto flex-shrink-0">
                {versions.map((v) => (
                  <button
                    key={v.version_number}
                    onClick={() => setSelectedVersion(v)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-100 transition-colors ${
                      selectedVersion?.version_number === v.version_number
                        ? 'bg-primary-50 border-l-2 border-l-primary'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-semibold ${
                        selectedVersion?.version_number === v.version_number ? 'text-primary' : 'text-gray-900'
                      }`}>
                        Version {v.version_number}
                      </span>
                      <span className="text-xs text-gray-400">
                        {v.created_at ? new Date(v.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                      </span>
                    </div>
                    {v.changed_by_name && (
                      <p className="text-xs text-gray-500 mt-0.5 truncate">by {v.changed_by_name}</p>
                    )}
                    {v.change_reason && (
                      <p className="text-xs text-gray-400 mt-1 truncate italic">{v.change_reason}</p>
                    )}
                  </button>
                ))}
              </div>

              {/* Right: version detail */}
              <div className="flex-1 flex flex-col min-w-0">
                {selectedVersion ? (
                  <>
                    <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50 flex-shrink-0">
                      <div>
                        <span className="text-sm font-semibold text-gray-700">
                          Version {selectedVersion.version_number}
                        </span>
                        <span className="text-xs text-gray-400 ml-3">
                          {selectedVersion.created_at ? new Date(selectedVersion.created_at).toLocaleString() : ''}
                        </span>
                      </div>
                      <button
                        onClick={() => setRevertTarget(selectedVersion.version_number)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                        </svg>
                        Revert to this version
                      </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-6 space-y-4">
                      <div className="flex items-center gap-4 flex-wrap">
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-1 tracking-wider">Category</p>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700">
                            {selectedVersion.category?.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-1 tracking-wider">Confidence</p>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${CONFIDENCE_COLORS[selectedVersion.confidence] || 'bg-gray-100 text-gray-600'}`}>
                            {selectedVersion.confidence}
                          </span>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-1 tracking-wider">Status</p>
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            selectedVersion.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                          }`}>
                            {selectedVersion.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1 tracking-wider">Title</p>
                        <p className="text-sm text-gray-900 font-medium">{selectedVersion.title}</p>
                      </div>

                      {selectedVersion.worker_tags?.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-1 tracking-wider">Worker Tags</p>
                          <div className="flex gap-1.5 flex-wrap">
                            {selectedVersion.worker_tags.map(tag => (
                              <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                                {tag.replace(/_/g, ' ')}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2 tracking-wider">
                          Decision Points ({selectedVersion.decision_points?.length || 0})
                        </p>
                        <div className="space-y-2">
                          {(selectedVersion.decision_points || []).map((dp, i) => (
                            <div key={i} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                              <p className="text-sm font-medium text-gray-900 mb-1">{dp.point}</p>
                              {dp.context && <p className="text-xs text-gray-600 mb-0.5"><span className="font-semibold">Context:</span> {dp.context}</p>}
                              {dp.rationale && <p className="text-xs text-gray-600 mb-0.5"><span className="font-semibold">Rationale:</span> {dp.rationale}</p>}
                              {dp.section_source && <p className="text-xs text-gray-400"><span className="font-semibold">Source:</span> {dp.section_source}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center justify-center flex-1">
                    <p className="text-gray-400 text-sm">Select a version to view details</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Revert Confirmation */}
      {revertTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-strong max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-almarai font-normal text-gray-900">Confirm Revert</h3>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              Revert <span className="font-semibold text-primary">{entryName}</span> to{' '}
              <span className="font-semibold">version {revertTarget}</span>?
            </p>
            <p className="text-sm text-gray-500 mb-6">
              The current version will be preserved in history.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setRevertTarget(null)} className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button onClick={handleRevert} disabled={reverting} className="px-5 py-2.5 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors shadow-md">
                {reverting ? 'Reverting...' : 'Revert'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default IKEntryHistoryModal;
